// @ts-nocheck
/**
 * Delivery Component
 * Manages delivery location display and synchronization with header-store-locator
 */
const NEARBY_STORES_LIMIT = 3;

class DeliveryComponent {
  constructor() {
    this.dbName = 'StoreLocatorDB';
    this.dbVersion = 2;
    this.db = null;
    this.selectedStore = null;
    this.userHasEnteredValue = false;
    this.initialUserInput = null;
    this.lastSearchLocation = null;

    this.selectors = {
      noDataContainer: '#delivery-location-no-data',
      locationInput: '#delivery-location-input',
      locationSuggestions: '#delivery-location-suggestions',
      findStoreButton: '#delivery-find-store-button',
      hasDataContainer: '#delivery-location-has-data',
      locationName: '#delivery-location-name',
      changeButton: '#location-popup-trigger',
      deliveryBox: '#delivery-box',
      nearbyStoresList: '#delivery-nearby-stores',
      storeCardTemplate: '#delivery-store-card-template',
      priorityMoreLink: '#priority-delivery-more-link',
      priorityMoreContent: '#priority-delivery-more-content'
    };

    this.init();
  }

  async init() {
    await this.initializeDatabase();
    await this.updateDeliveryDisplay();
    this.setupEventListeners();
    window.addEventListener('preferred-store-updated', () => this.updateDeliveryDisplay());
    document.addEventListener('location:changed', () => this.updateDeliveryDisplay());
  }

  async initializeDatabase() {
    return new Promise((resolve, reject) => {
      if (!('indexedDB' in window)) {
        console.warn('IndexedDB not supported');
        resolve(null);
        return;
      }

      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => {
        console.error('IndexedDB initialization failed:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains('selectedStore')) {
          const store = db.createObjectStore('selectedStore', { keyPath: 'id' });
          store.createIndex('timestamp', 'timestamp', { unique: false });
        }
        if (!db.objectStoreNames.contains('storeResults')) {
          const store = db.createObjectStore('storeResults', { keyPath: 'location' });
          store.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });
  }

  async getDatabase() {
    if (this.db) return this.db;
    await this.initializeDatabase();
    return this.db;
  }

  /**
   * Store search results for the customer's last location (array of stores).
   * @returns {Promise<Array<object>|null>}
   */
  async loadNearbyStores() {
    try {
      const db = await this.getDatabase();
      if (!db || !db.objectStoreNames.contains('storeResults')) return null;

      const customerLocation = await this.getLastSearchLocation();
      if (customerLocation) {
        const byLocation = await this.#getStoreResultsByKey(customerLocation);
        if (byLocation?.length) return byLocation;
      }

      return await this.#getMostRecentStoreResults();
    } catch (e) {
      console.error('Error loading nearby stores:', e);
      return null;
    }
  }

  /**
   * @param {string} locationKey
   * @returns {Promise<Array<object>|null>}
   */
  #getStoreResultsByKey(locationKey) {
    return new Promise((resolve) => {
      const db = this.db;
      if (!db) {
        resolve(null);
        return;
      }

      const transaction = db.transaction(['storeResults'], 'readonly');
      const objectStore = transaction.objectStore('storeResults');
      const request = objectStore.get(locationKey);

      request.onsuccess = () => {
        const result = request.result;
        if (!result?.stores?.length) {
          resolve(null);
          return;
        }
        if (result.expiresAt && Date.now() > result.expiresAt) {
          resolve(null);
          return;
        }
        resolve(result.stores);
      };

      request.onerror = () => resolve(null);
    });
  }

  /**
   * @returns {Promise<Array<object>|null>}
   */
  #getMostRecentStoreResults() {
    return new Promise((resolve) => {
      const db = this.db;
      if (!db) {
        resolve(null);
        return;
      }

      const transaction = db.transaction(['storeResults'], 'readonly');
      const objectStore = transaction.objectStore('storeResults');
      const request = objectStore.getAll();

      request.onsuccess = () => {
        const results = request.result;
        if (!results?.length) {
          resolve(null);
          return;
        }

        results.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
        const latest = results.find(
          (entry) =>
            entry?.stores?.length &&
            (!entry.expiresAt || Date.now() <= entry.expiresAt)
        );
        resolve(latest?.stores || null);
      };

      request.onerror = () => resolve(null);
    });
  }

  /**
   * @param {Array<object>} stores
   * @param {number} [limit]
   * @returns {Array<object>}
   */
  getNearestStores(stores, limit = NEARBY_STORES_LIMIT) {
    if (!Array.isArray(stores) || !stores.length) return [];

    return [...stores]
      .sort((a, b) => {
        const distA = this.#getStoreDistanceKm(a);
        const distB = this.#getStoreDistanceKm(b);
        if (distA == null && distB == null) return 0;
        if (distA == null) return 1;
        if (distB == null) return -1;
        return distA - distB;
      })
      .slice(0, limit);
  }

  /**
   * @param {object} store
   * @returns {number|null}
   */
  #getStoreDistanceKm(store) {
    if (!store) return null;
    const raw =
      store.distanceKm ??
      store.distance ??
      store._apiData?.distanceKm ??
      store._apiData?.distance ??
      null;
    if (raw === null || raw === '') return null;
    const n = typeof raw === 'number' ? raw : parseFloat(raw);
    return Number.isFinite(n) ? n : null;
  }

  async getLastSearchLocation() {
    try {
      const db = await this.getDatabase();
      if (!db) return null;

      return new Promise((resolve) => {
        const transaction = db.transaction(['selectedStore'], 'readonly');
        const store = transaction.objectStore('selectedStore');
        const request = store.get('lastSearch');

        request.onsuccess = () => {
          const location = request.result?.location;
          resolve(typeof location === 'string' && location.trim() ? location.trim() : null);
        };

        request.onerror = () => {
          resolve(null);
        };
      });
    } catch (e) {
      console.error('Error loading last search location:', e);
      return null;
    }
  }

  normalizeStoreData(storeData) {
    if (!storeData) return null;
    if (storeData._apiData || (storeData.address && typeof storeData.address === 'object')) {
      return this.convertApiStoreToInternalFormat(storeData);
    }
    if (storeData.name && typeof storeData.name === 'string' && storeData.name.trim() !== '') {
      return storeData;
    }
    return null;
  }

  convertApiStoreToInternalFormat(apiStore) {
    if (!apiStore) return null;
    const inner = apiStore._apiData || apiStore;
    const address = typeof inner.address === 'string' ? inner.address : (inner.address || apiStore.address);
    let addressString = '';
    let addressForDetails = '';

    if (typeof address === 'string') {
      addressString = address;
      addressForDetails = inner.addressForDetails || address;
    } else if (address && address.formatted && Array.isArray(address.formatted)) {
      addressString = address.formatted.join(', ');
      addressForDetails = address.formatted.join(', ');
    } else if (address) {
      const parts = [
        address.address1,
        address.address2,
        address.city,
        address.province,
        address.zip,
        address.country
      ].filter(Boolean);
      addressString = parts.join(', ');
      addressForDetails = parts.join(', ');
    }
    if (!addressString && apiStore.address && typeof apiStore.address === 'string') {
      addressString = apiStore.address;
      addressForDetails = apiStore.addressForDetails || apiStore.address;
    }

    let openingHours = {};
    const hoursSource = inner.openingHours ?? inner._apiData?.openingHours ?? apiStore.openingHours;
    if (hoursSource) {
      if (typeof hoursSource === 'string') {
        try {
          const parsed = JSON.parse(hoursSource);
          if (Array.isArray(parsed)) {
            parsed.forEach((day) => {
              if (day.day_of_week !== undefined) {
                const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
                const dayName = dayNames[day.day_of_week];
                if (dayName && day.start_time && day.end_time) {
                  openingHours[dayName] = `${this.formatTime(day.start_time)} - ${this.formatTime(day.end_time)}`;
                }
              }
            });
          } else if (typeof parsed === 'object' && parsed !== null) {
            openingHours = parsed;
          }
        } catch (e) {
          console.error('Error parsing opening hours:', e);
        }
      } else if (typeof hoursSource === 'object' && hoursSource !== null && Object.keys(hoursSource).length > 0) {
        openingHours = hoursSource;
      }
    }
    const hoursValue = inner._apiData?.openingHours?.value ?? inner.openingHours?.value;
    if (hoursValue && typeof hoursValue === 'string') {
      try {
        const parsed = JSON.parse(hoursValue);
        if (Array.isArray(parsed)) {
          parsed.forEach((day) => {
            if (day.day_of_week !== undefined) {
              const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
              const dayName = dayNames[day.day_of_week];
              if (dayName && day.start_time && day.end_time) {
                openingHours[dayName] = `${this.formatTime(day.start_time)} - ${this.formatTime(day.end_time)}`;
              }
            }
          });
        }
      } catch (e) { }
    }

    const email = inner._apiData?.contactEmail?.value ?? inner.contactEmail?.value ?? inner.email ?? apiStore.email ?? '';
    const addressObj = inner._apiData?.address ?? (typeof inner.address === 'object' ? inner.address : null);
    const phone = addressObj?.phone ?? inner.phone ?? inner.phoneNumber ?? apiStore.phone ?? apiStore.phoneNumber ?? '';
    const distanceKm = apiStore.distanceKm ?? apiStore.distance ?? inner.distance ?? inner._apiData?.distance ?? null;
    const numDistance = distanceKm !== null && distanceKm !== '' ? (typeof distanceKm === 'number' ? distanceKm : parseFloat(distanceKm)) : null;

    return {
      name: (inner.name || apiStore.name || '').trim() || '',
      address: addressString,
      addressForDetails: addressForDetails || addressString,
      openingHours: Object.keys(openingHours).length ? openingHours : (inner.openingHours && typeof inner.openingHours === 'object' ? inner.openingHours : openingHours),
      distanceKm: numDistance,
      phone: phone || null,
      email: email || null,
      _apiData: apiStore._apiData || apiStore
    };
  }

  formatTime(timeStr) {
    if (!timeStr) return '';
    const parts = timeStr.split(':');
    if (parts.length === 0) return '';
    const hour = parseInt(parts[0], 10);
    const minute = parts[1] || '00';
    const period = hour >= 12 ? 'pm' : 'am';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minute}${period}`;
  }

  async updateDeliveryDisplay() {
    const storesRaw = await this.loadNearbyStores();
    const customerLocation = await this.getLastSearchLocation();
    const allStores = Array.isArray(storesRaw) ? storesRaw : [];

    const wrapper = document.querySelector('.delivery-wrapper');
    const productPricingModel = wrapper?.getAttribute('data-product-pricing-model')?.trim().toLowerCase() || 'electrical';

    let targetStores = allStores;

    if (productPricingModel) {
      targetStores = allStores.filter((store) => {
        let storePricingModels = this.#getStorePricingModel(store);

        if (storePricingModels.length === 0) {
          storePricingModels = ['electrical'];
        }

        return storePricingModels.includes(productPricingModel);
      });
    }

    const nearestStores = this.getNearestStores(targetStores, NEARBY_STORES_LIMIT);

    this.selectedStore = nearestStores[0] || null;
    this.nearbyStores = nearestStores;

    const noDataContainer = document.querySelector(this.selectors.noDataContainer);
    const hasDataContainer = document.querySelector(this.selectors.hasDataContainer);
    const changeButton = document.querySelector(this.selectors.changeButton);
    const deliveryBox = document.querySelector(this.selectors.deliveryBox);
    const nearbyStoresList = document.querySelector(this.selectors.nearbyStoresList);
    const locationNameEl = document.querySelector(this.selectors.locationName);
    if (locationNameEl) {
      const modalInput = document.querySelector('#store-location-input');
      locationNameEl.textContent = modalInput.value || customerLocation;
    }

    const hasValidStores = nearestStores.length > 0;

    if (hasValidStores) {
      if (noDataContainer) noDataContainer.style.display = 'none';
      if (hasDataContainer) hasDataContainer.style.display = 'flex';
      if (changeButton) changeButton.style.display = 'block';

      if (deliveryBox) deliveryBox.classList.remove('hidden');
      const pickupSection = document.querySelector('#delivery-pickup-section');
      if (pickupSection) pickupSection.classList.remove('hidden');
      
      if (nearbyStoresList) {
        this.renderNearbyStores(nearestStores, nearbyStoresList);
      }
    } else {
      if (noDataContainer) noDataContainer.style.display = 'block';
      if (hasDataContainer) hasDataContainer.style.display = 'none';
      if (changeButton) changeButton.style.display = 'none';

      if (deliveryBox) deliveryBox.classList.add('hidden');
      if (nearbyStoresList) nearbyStoresList.innerHTML = '';
      const pickupSection = document.querySelector('#delivery-pickup-section');
      if (pickupSection) pickupSection.classList.add('hidden');
    }
  }

  /**
   * @param {Array<object>} stores
   * @param {HTMLElement} container
   */
  renderNearbyStores(stores, container) {
    const template = document.querySelector(this.selectors.storeCardTemplate);
    if (!template || !(template instanceof HTMLTemplateElement)) return;

    container.innerHTML = '';

    stores.forEach((rawStore, index) => {
      const store = this.normalizeStoreData(rawStore);
      if (!store?.name) return;

      const card = template.content.firstElementChild?.cloneNode(true);
      if (!(card instanceof HTMLElement)) return;

      card.style.display = '';
      card.dataset.storeIndex = String(index);
      this.#fillStoreCard(card, store);
      this.#bindStoreCardMoreToggle(card);
      container.appendChild(card);
    });
  }

  /**
   * @param {HTMLElement} card
   * @param {object} store
   */
  #fillStoreCard(card, store) {
    const storeNameEl = card.querySelector('.delivery-store-details__store-name');
    const storeDistanceEl = card.querySelector('.delivery-store-details__store-distance');
    const storeHoursEl = card.querySelector('.delivery-store-details__store-hours');
    const phoneEl = card.querySelector('.delivery-store-phone');
    const emailEl = card.querySelector('.delivery-store-email');
    const phoneRow = card.querySelector('.delivery-store-phone-row');
    const emailRow = card.querySelector('.delivery-store-email-row');
    const hoursListEl = card.querySelector('.delivery-store-opening-hours');
    const specialHoursListEl = card.querySelector('.delivery-store-special-hours');

    if (storeNameEl) storeNameEl.textContent = store.name || '';

    const distance = store.distanceKm ?? store.distance ?? this.#getStoreDistanceKm(store);
    if (storeDistanceEl && distance != null && distance !== '') {
      const km = typeof distance === 'number' ? distance : parseFloat(distance);
      storeDistanceEl.textContent = Number.isFinite(km) ? `${km}km` : '';
    } else if (storeDistanceEl) {
      storeDistanceEl.textContent = '';
    }

    if (storeHoursEl && store.openingHours) {
      const todayHours = this.getTodayOpeningHours(store.openingHours);
      storeHoursEl.innerHTML = todayHours
        ? `<strong>Open Today:</strong> ${todayHours}`
        : 'Hours not available';
    } else if (storeHoursEl) {
      storeHoursEl.textContent = '';
    }

    const phone =
      store.phone ?? store._apiData?.address?.phone ?? store._apiData?._apiData?.address?.phone ?? '';
    if (phoneEl && phoneRow) {
      phoneEl.innerHTML = '<a href="tel:' + phone.replace(/"/g, '&quot;') + '">' + (phone.replace(/</g, '&lt;').replace(/>/g, '&gt;')) + '</a>';
      phoneRow.style.display = phone ? 'flex' : 'none';
    }

    const email =
      store.email ?? store._apiData?.contactEmail?.value ?? store._apiData?._apiData?.contactEmail?.value ?? '';
    if (emailEl && emailRow) {
      emailEl.innerHTML = '<a href="mailto:' + email.replace(/"/g, '&quot;') + '">' + (email.replace(/</g, '&lt;').replace(/>/g, '&gt;')) + '</a>';
      emailRow.style.display = email ? 'flex' : 'none';
    }

    const hours =
      store.openingHours && Object.keys(store.openingHours).length > 0
        ? store.openingHours
        : store._apiData?.openingHours && typeof store._apiData.openingHours === 'object'
          ? store._apiData.openingHours
          : null;
    if (hoursListEl) {
      hoursListEl.innerHTML = hours ? this.renderOpeningHoursList(hours) : '';
    }

    // Render special hours (holiday / date-specific)
    this.renderSpecialHours(specialHoursListEl, store);
  }

  renderSpecialHours(specialHoursEl, store) {
    if (!specialHoursEl) return;

    const targetStore = store || this.selectedStore;
    if (!targetStore) {
        specialHoursEl.innerHTML = '';
        specialHoursEl.style.display = 'none';
        return;
    }

    let specialRawArray = null;

    const openingHoursSource =
        targetStore?.openingHours?.value ||
        targetStore?._apiData?.openingHours?.value ||
        targetStore?._apiData?._apiData?.openingHours?.value ||
        targetStore?.openingHours ||
        targetStore?._apiData?.openingHours;

    if (openingHoursSource) {
        const rawVal = typeof openingHoursSource === 'string' ? openingHoursSource : (openingHoursSource.value || '');
        if (rawVal) {
            try {
                specialRawArray = JSON.parse(rawVal);
            } catch (e) { }
        } else if (Array.isArray(openingHoursSource)) {
            specialRawArray = openingHoursSource;
        } else if (openingHoursSource.value && Array.isArray(openingHoursSource.value)) {
            specialRawArray = openingHoursSource.value;
        }
    }

    const specials = Array.isArray(specialRawArray)
        ? specialRawArray.filter(h => h && h.date && String(h.date).trim() !== '')
        : [];

    if (specials.length === 0) {
        specialHoursEl.innerHTML = '';
        specialHoursEl.style.display = 'none';
        return;
    }

    const weekdaysShort = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const monthsShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                         'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    // Local time formatter: "08:30:00" -> "8:30am", "17:00:00" -> "5:00pm"
    const formatTime = (timeStr) => {
        if (!timeStr) return '';
        const [hStr, mStr] = String(timeStr).split(':');
        let h = parseInt(hStr, 10);
        const m = parseInt(mStr, 10) || 0;
        if (!Number.isFinite(h)) return timeStr;
        const period = h >= 12 ? 'pm' : 'am';
        h = h % 12;
        if (h === 0) h = 12;
        const mm = ':' + String(m).padStart(2, '0');
        return `${h}${mm}${period}`;
    };

    specials.sort((a, b) => {
        const da = String(a.date).split(' ')[0];
        const db = String(b.date).split(' ')[0];
        return da.localeCompare(db);
    });

    specialHoursEl.innerHTML = specials.map(item => {
        const datePart = String(item.date).split(' ')[0];
        const [y, m, d] = datePart.split('-').map(Number);
        const dt = new Date(y, m - 1, d);

        const weekday = weekdaysShort[dt.getDay()];
        const dateStr = d + ' ' + monthsShort[m - 1];

        const hasHours = item.start_time && item.end_time;
        const hoursStr = hasHours
            ? formatTime(item.start_time) + ' – ' + formatTime(item.end_time)
            : 'Closed';

        return `<div class="delivery-store-details__hours-row">
          <span>${weekday}</span>
          <span>${dateStr}</span>
          <span>${hoursStr}</span>
        </div>`;
    }).join('');

    specialHoursEl.style.display = '';
  }

  /**
   * @param {HTMLElement} card
   */
  #bindStoreCardMoreToggle(card) {
    const link = card.querySelector('.delivery-store-details__more-link');
    const content = card.querySelector('.delivery-store-details__more-content');
    if (!link || !content) return;

    link.setAttribute('aria-expanded', 'false');
    content.classList.add('hidden');

    const contentId = `delivery-store-more-${card.dataset.storeIndex || '0'}`;
    content.id = contentId;
    link.setAttribute('aria-controls', contentId);

    link.addEventListener('click', () => {
      const isExpanded = link.getAttribute('aria-expanded') === 'true';
      link.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');
      content.classList.toggle('hidden', isExpanded);
    });
  }

  extractLocationFromStore(store) {
    if (store.addressForDetails) {
      const parts = store.addressForDetails.split(',');
      if (parts.length >= 2) {
        // Return suburb/city and state
        return parts.slice(-2).map(p => p.trim()).join(', ');
      }
      return store.addressForDetails;
    }
    if (store.address) {
      const parts = store.address.split(',');
      if (parts.length >= 2) {
        return parts.slice(-2).map(p => p.trim()).join(', ');
      }
      return store.address;
    }
    return store.name || '';
  }

  renderOpeningHoursList(openingHours) {
    if (!openingHours || typeof openingHours !== 'object') return '';
    const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const keys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    return keys.map((key, i) => {
      const hours = openingHours[key] || 'Closed';
      return `<div class="delivery-store-details__hours-row"><span>${dayNames[i]}</span><span>${hours}</span></div>`;
    }).join('');
  }

  #getStorePricingModel(store) {
    if (!store) return [];
    const inner = store._apiData || store;

    const cand = inner.priceModel ?? store.priceModel;
    let value = null;
    if (cand != null) {
      if (typeof cand === 'object' && cand.value !== undefined) {
        value = cand.value;
      } else {
        value = cand;
      }
    }

    if (value == null || value === '') return [];

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) {
            return parsed.map(item => String(item).trim().toLowerCase()).filter(Boolean);
          }
        } catch (e) {
          console.warn('Error parsing priceModel JSON:', e);
        }
      }
      return [trimmed.toLowerCase()];
    }

    if (Array.isArray(value)) {
      return value.map(item => String(item).trim().toLowerCase()).filter(Boolean);
    }

    return [String(value).trim().toLowerCase()];
  }

  /**
   * Returns true if the store is open on at least one day of the week.
   * Stores that are closed every day should not be offered for pickup.
   * @param {object} rawStore
   * @returns {boolean}
   */
  #isStoreOpenAnyDay(rawStore) {
    const store = this.normalizeStoreData(rawStore);
    const hours = store?.openingHours;
    if (!hours || typeof hours !== 'object') return false;
    const keys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    return keys.some((key) => {
      const value = hours[key];
      if (!value) return false;
      if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        return normalized !== '' && normalized !== 'closed';
      }
      return true;
    });
  }

  getTodayOpeningHours(openingHours) {
    if (!openingHours || typeof openingHours !== 'object') return null;

    const now = new Date();
    const dayIndex = now.getDay(); // 0 = Sunday, 6 = Saturday
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const todayName = dayNames[dayIndex];

    return openingHours[todayName] || null;
  }

  togglePriorityMoreContent() {
    const link = document.querySelector(this.selectors.priorityMoreLink);
    const content = document.querySelector(this.selectors.priorityMoreContent);
    if (!link || !content) return;
    const isExpanded = link.getAttribute('aria-expanded') === 'true';
    link.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');
    content.classList.toggle('hidden', isExpanded);
  }

  /**
   * Setup event listeners
   */
  setupEventListeners() {
    const findButton = document.querySelector(this.selectors.findStoreButton);
    if (findButton) {
      findButton.addEventListener('click', () => {
        this.handleFindStore();
      });
    }

    const changeButton = document.querySelector(this.selectors.changeButton);
    if (changeButton) {
      changeButton.addEventListener('click', () => {
        this.handleChangeStore();
      });
    }

    const priorityMoreLink = document.querySelector(this.selectors.priorityMoreLink);
    if (priorityMoreLink) {
      priorityMoreLink.addEventListener('click', () => {
        this.togglePriorityMoreContent();
      });
    }

    const locationInput = document.querySelector(this.selectors.locationInput);
    if (locationInput) {
      locationInput.addEventListener('input', (e) => {
        if (e.target instanceof HTMLInputElement) {
          this.initialUserInput = e.target.value;
          this.userHasEnteredValue = true;
        }
      });

      locationInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.handleFindStore();
        }
      });
    }

    const locationIcon = document.querySelector('.delivery-location__input-icon');
    if (locationIcon) {
      locationIcon.addEventListener('click', () => {
        const input = document.querySelector(this.selectors.locationInput);
        if (input && input instanceof HTMLInputElement) {
          this.getCurrentLocation(input);
        }
      });
    }

    // this.setupGoogleMapsAutocomplete();
  }

  handleFindStore() {
    this.openSetStoreModal();
  }

  handleChangeStore() {
    this.openSetStoreModal();
  }

  getVisibleStoreLocator() {
    const components = document.querySelectorAll('store-locator-component');
    for (const comp of components) {
      if (comp.getClientRects().length > 0) return comp;
    }
    return components[0] || null;
  }

  openSetStoreModal(location = null) {
    const storeLocatorComponent = this.getVisibleStoreLocator();
    if (storeLocatorComponent) {
      if (typeof storeLocatorComponent.openSetStoreModal === 'function') {
        storeLocatorComponent.openSetStoreModal();
      } else {
        const component = storeLocatorComponent.shadowRoot?.querySelector('store-locator-component') || storeLocatorComponent;
        if (component && typeof component.openSetStoreModal === 'function') {
          component.openSetStoreModal();
        }
      }
      if (location) {
        const modalInput = document.querySelector('#store-location-input');
        if (modalInput && modalInput instanceof HTMLInputElement) {
          modalInput.value = location;
          const findButton = document.querySelector('.set-store__find-button');
          if (findButton) findButton.click();
        }
      }
    } else {
      const modal = document.querySelector('#set-store-modal');
      if (modal) {
        const dialog = modal.querySelector('dialog-component');
        if (dialog && typeof dialog.showDialog === 'function') {
          dialog.showDialog();
        } else {
          const dialogEl = modal.querySelector('dialog');
          if (dialogEl && typeof dialogEl.showModal === 'function') {
            dialogEl.showModal();
          }
        }
        if (location) {
          const modalInput = document.querySelector('#store-location-input');
          if (modalInput && modalInput instanceof HTMLInputElement) {
            modalInput.value = location;
          }
        }
      }
    }
  }

  async getCurrentLocation(inputElement) {
    if (!(inputElement instanceof HTMLInputElement)) return;

    if (!navigator.geolocation) {
      console.warn('Geolocation not supported');
      return;
    }

    const isGoogleMapsReady = await this.waitForGoogleMaps();
    if (!isGoogleMapsReady) {
      console.warn('Google Maps API not available');
      return;
    }

    const originalPlaceholder = inputElement.placeholder;
    inputElement.placeholder = 'Getting your location...';
    inputElement.disabled = true;

    try {
      const position = await this.getGeolocationPosition();
      const { latitude: lat, longitude: lng } = position.coords;

      const geocoder = new window.google.maps.Geocoder();
      const geocodeResult = await this.reverseGeocodeWithGoogleMaps(geocoder, lat, lng);
      if (!this.userHasEnteredValue) {
        inputElement.value = geocodeResult.formatted_address || geocodeResult.name || '';
      }
      inputElement.disabled = false;
      inputElement.placeholder = originalPlaceholder;

      this.lastSearchLocation = geocodeResult.formatted_address || geocodeResult.name || '';
      await this.searchStoresWithLatLng(lat, lng);
    } catch (error) {
      console.error('Error getting location:', error);
      inputElement.disabled = false;
      inputElement.placeholder = originalPlaceholder;
      this.handleGeolocationError(error);
    }
  }

  async waitForGoogleMaps() {
    if (typeof window.google !== 'undefined' && window.google.maps?.Geocoder) {
      return true;
    }

    const maxWait = 5000;
    const startTime = Date.now();
    while (Date.now() - startTime < maxWait) {
      if (typeof window.google !== 'undefined' && window.google.maps?.Geocoder) {
        return true;
      }
      await new Promise(resolve => requestAnimationFrame(resolve));
    }
    return false;
  }

  getGeolocationPosition() {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        resolve,
        reject,
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    });
  }

  reverseGeocodeWithGoogleMaps(geocoder, lat, lng) {
    return new Promise((resolve, reject) => {
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === window.google.maps.GeocoderStatus.OK && results?.[0]) {
          resolve(results[0]);
        } else if (status === window.google.maps.GeocoderStatus.REQUEST_DENIED) {
          reject(new Error('REQUEST_DENIED'));
        } else if (status === window.google.maps.GeocoderStatus.OVER_QUERY_LIMIT) {
          reject(new Error('OVER_QUERY_LIMIT'));
        } else if (status === window.google.maps.GeocoderStatus.ZERO_RESULTS) {
          reject(new Error('ZERO_RESULTS'));
        } else {
          reject(new Error(`Geocoding failed: ${status}`));
        }
      });
    });
  }

  handleGeolocationError(error) {
    if (error.code === 1 || error.code === error.PERMISSION_DENIED) {
      console.warn('Location permission denied');
    } else if (error.code === 2 || error.code === error.POSITION_UNAVAILABLE) {
      console.warn('Location information unavailable');
    } else if (error.code === 3 || error.code === error.TIMEOUT) {
      console.warn('Location request timeout');
    } else if (error.message?.includes('REQUEST_DENIED')) {
      console.error('Google Maps API key error (REQUEST_DENIED)');
    } else if (error.message?.includes('OVER_QUERY_LIMIT')) {
      console.warn('Google Maps API quota exceeded');
    } else {
      console.warn('Unknown geolocation error:', error.message);
    }
  }

  async searchStoresWithLatLng(lat, lng) {
    const input = document.querySelector(this.selectors.locationInput);
    const location = input && input instanceof HTMLInputElement ? input.value.trim() : '';

    if (!location) {
      console.warn('Please enter a postcode or suburb');
      return;
    }
    this.openSetStoreModal(location);
    const modalInput = document.querySelector('#store-location-input');
    if (modalInput && modalInput instanceof HTMLInputElement) {
      modalInput.value = location;
    }
    const findButton = document.querySelector('.set-store__find-button');
    if (findButton) findButton.click();
  }

  async searchStores() {
    const input = document.querySelector(this.selectors.locationInput);
    const location = input && input instanceof HTMLInputElement ? input.value.trim() : '';
    if (!location) {
      console.warn('Please enter a postcode or suburb');
      return;
    }
    this.openSetStoreModal(location);
    const modalInput = document.querySelector('#store-location-input');
    if (modalInput && modalInput instanceof HTMLInputElement) {
      modalInput.value = location;
    }
    const findButton = document.querySelector('.set-store__find-button');
    if (findButton) findButton.click();
  }

  filterStoresByGeocodedLocation(stores, geocodedLocation) {
    if (!geocodedLocation?.id || !Array.isArray(stores)) {
      return stores;
    }

    const extractId = (id) => {
      if (typeof id === 'string' && id.startsWith('gid://')) {
        const match = id.match(/\/(\d+)$/);
        return match ? match[1] : id;
      }
      return String(id);
    };

    const geocodedId = extractId(geocodedLocation.id);
    return stores.filter(store => {
      const storeId = store?._apiData?.id || store?.id || null;
      if (!storeId) return true;
      const extractedStoreId = extractId(storeId);
      return extractedStoreId !== geocodedId;
    });
  }

  setupGoogleMapsAutocomplete() {
    if (typeof google === 'undefined' || !google.maps || !google.maps.places) {
      console.warn('Google Maps API not loaded for delivery autocomplete');
      return;
    }

    const input = document.querySelector(this.selectors.locationInput);
    const suggestions = document.querySelector(this.selectors.locationSuggestions);

    if (!input) {
      console.error('Cannot find delivery-location-input element');
      return;
    }
    const autocompleteOptions = {
      types: ['postal_code', 'locality', 'sublocality'],
      componentRestrictions: { country: 'au' },
      fields: ['place_id', 'name', 'geometry', 'address_components']
    };

    const autocomplete = new google.maps.places.Autocomplete(input, autocompleteOptions);
    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (place && place.geometry) {
        if (place.formatted_address) {
          input.value = place.formatted_address;
        } else if (place.name) {
          input.value = place.name;
        }
        if (suggestions) {
          suggestions.innerHTML = '';
          suggestions.classList.remove('has-suggestions');
        }
      }
    });
    const debounceFn = typeof debounce === 'function' ? debounce : (func, delay) => {
      let timeoutId;
      return function (...args) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func.apply(this, args), delay);
      };
    };
    const fetchSuggestions = debounce((inputValue) => {
      if (!suggestions) return;

      const service = new google.maps.places.AutocompleteService();
      service.getPlacePredictions(
        {
          input: inputValue,
          ...autocompleteOptions
        },
        (predictions, status) => {
          suggestions.innerHTML = '';
          suggestions.classList.remove('has-suggestions');
          if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
            predictions.forEach((prediction) => {
              const div = document.createElement('div');
              div.textContent = prediction.description;
              div.className = 'suggestion-item';
              div.onclick = (e) => {
                e.preventDefault();
                e.stopPropagation();
                const selectedValue = prediction.description;
                input.value = selectedValue;
                this.userHasEnteredValue = true;
                this.initialUserInput = selectedValue;
                suggestions.innerHTML = '';
                suggestions.classList.remove('has-suggestions');
                input.blur();
                if (input.value !== selectedValue) {
                  input.value = selectedValue;
                }
                this.searchStores();
              };
              suggestions.appendChild(div);
            });
            if (predictions.length > 0) {
              suggestions.classList.add('has-suggestions');
            }
          }
        }
      );
    }, 300);
    if (input && suggestions) {
      input.addEventListener('input', function () {
        if (input.value.trim()) {
          fetchSuggestions(input.value);
        } else {
          suggestions.innerHTML = '';
          suggestions.classList.remove('has-suggestions');
        }
      });
    }
    document.addEventListener('click', (e) => {
      if (suggestions && !input.contains(e.target) && !suggestions.contains(e.target)) {
        suggestions.innerHTML = '';
        suggestions.classList.remove('has-suggestions');
      }
    });
  }

}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new DeliveryComponent();
  });
} else {
  new DeliveryComponent();
}
